import { createAdminClient } from "@/utils/supabase/admin";
import { createClient } from "@/utils/supabase/client";
import { createClient as createServerClient } from "@/utils/supabase/server";

export function generateApiKey(): string {
	const timestamp = Date.now().toString(36);
	const randomPart = Math.random().toString(36).substring(2, 15);
	const randomPart2 = Math.random().toString(36).substring(2, 15);
	return `projme_${timestamp}_${randomPart}${randomPart2}`;
}

export function isValidApiKeyFormat(apiKey: string): boolean {
	return /^projme_[a-z0-9]+_[a-z0-9]+$/.test(apiKey);
}

export async function generateFormApiKey(formId: string): Promise<{
	success: boolean;
	apiKey?: string;
	error?: string;
}> {
	try {
		const supabase = createClient();
		const {
			data: { user },
		} = await supabase.auth.getUser();

		if (!user) {
			return { success: false, error: "Authentication required" };
		}

		const apiKey = generateApiKey();

		// Ключ хранится только в form_api_keys (t_29c72dec): в forms его больше нет
		const { error } = await supabase
			.from("form_api_keys")
			.upsert({ form_id: formId, api_key: apiKey }, { onConflict: "form_id" });

		if (error) {
			console.error("Error generating API key:", error);
			return { success: false, error: "Failed to generate API key" };
		}

		const { error: flagError } = await supabase
			.from("forms")
			.update({ api_enabled: true, updated_at: new Date().toISOString() })
			.eq("id", formId)
			.eq("user_id", user.id);

		if (flagError) {
			console.error("Error updating form API flag:", flagError);
			return { success: false, error: "Failed to update API settings" };
		}


		if (error) {
			console.error("Error generating API key:", error);
			return { success: false, error: "Failed to generate API key" };
		}

		return { success: true, apiKey };
	} catch (error) {
		console.error("Error generating API key:", error);
		return { success: false, error: "An unexpected error occurred" };
	}
}

export async function revokeFormApiKey(formId: string): Promise<{
	success: boolean;
	error?: string;
}> {
	try {
		const supabase = createClient();
		const {
			data: { user },
		} = await supabase.auth.getUser();

		if (!user) {
			return { success: false, error: "Authentication required" };
		}

		const { error } = await supabase
			.from("form_api_keys")
			.delete()
			.eq("form_id", formId);

		if (error) {
			console.error("Error revoking API key:", error);
			return { success: false, error: "Failed to revoke API key" };
		}

		const { error: flagError } = await supabase
			.from("forms")
			.update({ api_enabled: false, updated_at: new Date().toISOString() })
			.eq("id", formId)
			.eq("user_id", user.id);

		if (flagError) {
			console.error("Error updating form API flag:", flagError);
			return { success: false, error: "Failed to update API settings" };
		}


		if (error) {
			console.error("Error revoking API key:", error);
			return { success: false, error: "Failed to revoke API key" };
		}

		return { success: true };
	} catch (error) {
		console.error("Error revoking API key:", error);
		return { success: false, error: "An unexpected error occurred" };
	}
}

export async function toggleFormApiEnabled(
	formId: string,
	enabled: boolean
): Promise<{
	success: boolean;
	error?: string;
}> {
	try {
		const supabase = createClient();
		const {
			data: { user },
		} = await supabase.auth.getUser();

		if (!user) {
			return { success: false, error: "Authentication required" };
		}

		const { error } = await supabase
			.from("forms")
			.update({
				api_enabled: enabled,
				updated_at: new Date().toISOString(),
			})
			.eq("id", formId)
			.eq("user_id", user.id);

		if (error) {
			console.error("Error toggling API status:", error);
			return { success: false, error: "Failed to update API status" };
		}

		return { success: true };
	} catch (error) {
		console.error("Error toggling API status:", error);
		return { success: false, error: "An unexpected error occurred" };
	}
}

export async function getFormByApiKey(apiKey: string): Promise<{
	success: boolean;
	form?: any;
	error?: string;
}> {
	try {
		if (!isValidApiKeyFormat(apiKey)) {
			return { success: false, error: "Invalid API key format" };
		}

		// Ключ лежит в form_api_keys, закрытой RLS для всех, кроме владельца: вызывающий
		// доказывает право самим ключом, поэтому читаем сервисным клиентом (t_29c72dec).
		const admin = createAdminClient();

		const { data: keyRow, error: keyError } = await admin
			.from("form_api_keys")
			.select("form_id")
			.eq("api_key", apiKey)
			.maybeSingle();

		if (keyError || !keyRow?.form_id) {
			return { success: false, error: "Invalid API key or form not found" };
		}

		const { data: form, error } = await admin
			.from("forms")
			.select("*")
			.eq("id", keyRow.form_id)
			.eq("api_enabled", true)
			.single();

		if (error || !form) {
			return { success: false, error: "Invalid API key or form not found" };
		}

		return { success: true, form };
	} catch (error) {
		console.error("Error getting form by API key:", error);
		return { success: false, error: "An unexpected error occurred" };
	}
}

export async function validateFormApiAccess(
	apiKey: string,
	formId: string
): Promise<{
	success: boolean;
	form?: any;
	error?: string;
}> {
	try {
		if (!isValidApiKeyFormat(apiKey)) {
			return { success: false, error: "Invalid API key format" };
		}

		const admin = createAdminClient();

		// Ключ должен принадлежать именно этой форме
		const { data: keyRow, error: keyError } = await admin
			.from("form_api_keys")
			.select("form_id")
			.eq("api_key", apiKey)
			.eq("form_id", formId)
			.maybeSingle();

		if (keyError || !keyRow) {
			return { success: false, error: "Invalid API key for this form" };
		}

		const { data: form, error } = await admin
			.from("forms")
			.select("*")
			.eq("id", formId)
			.eq("api_enabled", true)
			.single();

		if (error || !form) {
			return { success: false, error: "API access is disabled for this form" };
		}

		return { success: true, form };
	} catch (error) {
		console.error("Error validating API access:", error);
		return { success: false, error: "An unexpected error occurred" };
	}
}
